import {
    Bounds,
    Cache,
    Distance,
    Line,
    Logger,
    Matrix,
    namespace,
    Point,
} from '../../../../../FrontendBundle/Resources/js/ofcs';
import Tree from './lineTree/tree';
import Model from './model';
import ValidatorInterface from './validator/validatorInterface';

namespace('Ofcs.Editor.Item');

/**
 * @class AbstractItem
 * @extends Model
 */
export default window.Ofcs.Editor.Item.AbstractItem = Model.extend({

    /**
     * Defines that item causes collisions
     * @property {Boolean} supportsCollisions
     */
    supportsCollisions: false,

    /**
     * Defines that item causes bidirectional collisions
     * Use when safety margins are not distributed equally around item
     * @property {Boolean} supportsCollisions
     */
    supportsBidirectionalCollisions: false,

    /**
     * Defines that item collisions include safety margins.
     * @property {Boolean} supportsMarginCollisions
     */
    supportsMarginCollisions: true,

    /**
     * Defines that item causes collisions with foam
     * @property {Boolean} supportsOutOfFoam
     */
    supportsOutOfFoam: false,

    /**
     * Defines that item stays within table bounds
     *
     * possible values:
     * edge - will allow item bag to touch table edges
     * inside - will keep bag and margin inside of table bounds
     *
     * @property {String} supportsInsideTable
     */
    supportsInsideTable: '',

    /**
     * Defines that item can be aligned on guides
     * @property {Boolean} supportsCollisions
     */
    supportsGuides: false,

    /**
     * Defines that item is rotateable
     * @property {Boolean} supportsRotation
     */
    supportsRotation: false,

    /**
     * Defines if this item is allowed to freely rotate
     * @property {boolean} supportsFreeRotation
     */
    supportsFreeRotation: true,

    /**
     * Defines the available rotation steps, empty array or null if only free rotation is allowed
     * By default there will be a step every 15°
     * @property {number[]} supportedRotationSteps
     */
    supportedRotationSteps: new Array(24).fill(0).map((value, idx) => idx * 15),

    /**
     * Defines that item is able to be rotated initially (not visible to user)
     * @property {Boolean} supportsInitialRotation
     */
    supportsInitialRotation: false,

    /**
     * Defines that item width is resizeable
     * @property {Boolean} supportsWidth
     */
    supportsWidth: false,

    /**
     * Defines that item height is resizeable
     * @property {Boolean} supportsHeight
     */
    supportsHeight: false,

    /**
     * Defines that item is positionable
     * @property {Boolean} supportsPositioning
     */
    supportsPositioning: false,

    /**
     * Defines that item has depth
     * @property {Boolean} supportsDepth
     */
    supportsDepth: false,

    /**
     * Defines that item supports flipping
     * @property {Boolean} supportsFlipping
     */
    supportsFlipping: false,

    /**
     * Defines that item supports align
     * @property {Boolean} supportsAlign
     */
    supportsAlign: false,

    /**
     * Defines that item supports versions
     * @property {Boolean} supportsVersions
     */
    supportsVersions: false,

    /**
     * Defines that item supports availability
     * @property {Boolean} supportsVersions
     */
    supportsAvailability: false,

    /**
     * Defines that an item supports covers
     * @property {Boolean} supportsOptions
     */
    supportsCover: false,

    /**
     * Defines that an item supports sealing
     * @property {Boolean} supportsSealing
     */
    supportsSealing: false,

    /**
     * Defines that an item can be moved
     * @property {Boolean} supportsMove
     */
    supportsMove: true,

    /**
     * Defines that an item can be copied
     * @property {Boolean} supportsCopying
     */
    supportsCopying: true,

    /**
     * Defines that an item can be selected
     * amongst other items
     * @property {Boolean} supportsMultiselection
     */
    supportsMultiselection: true,

    /**
     * Parent item, which contains this model
     * @property {Ofcs.Editor.Item.AbstractItem} parent
     */
    parent: null,

    /**
     * Validators
     * @property {Array} validators
     */
    validators: null,

    /**
     * Triggered if item is selected
     *
     * @event select
     * @param {Object} e Event object
     * @param {Boolean} e.multiselect If event is a multiselect
     */

    /**
     * Triggered if item gets deselected
     *
     * @event deselect
     */

    initialize() {
        this.flippedPaths = {};
        this.validators = [];
        this.cache = new Cache();
    },

    getClassification() {
        return this.getType();
    },

    getType() {
        return this.type || this.get('type');
    },

    /**
     * Parses response data into attributes
     *
     * @param {Object} response Response hash
     * @return {Object} Attribute hash
     */
    parse(response) {
        if (response.x !== undefined && response.y !== undefined) {
            response.position = new Point(response.x, response.y);
        }

        if (response.rotation) {
            response.rotation = parseFloat(response.rotation);
        }

        return response;
    },

    set(key, value, optionsParam) {
        const { attrs, options } = this._normalizeSetParameters(key, value, optionsParam);
        let saneAttrs = { ...attrs };

        this.clearCache(attrs);

        // normalize rotation
        if (attrs.rotation !== undefined) {
            saneAttrs = this._sanitizeRotationValue(saneAttrs);
        }

        // Fix position if rotation center is given
        if (attrs.rotation !== undefined && options.center) {
            saneAttrs = this._updatePositionByRotationCenter(saneAttrs, options.center);
        }

        Model.prototype.set.call(this, saneAttrs, options);
    },

    _normalizeSetParameters(key, value, optionsParam) {
        let attrs = {};
        let options = optionsParam;

        // Handle both `"key", value` and `{key: value}` -style arguments.
        if (typeof key === 'object') {
            attrs = key;
            options = value;
        } else {
            attrs[key] = value;
        }

        options = options || {};

        return { attrs, options };
    },

    /**
     * Returns the root item of this item.
     *
     * @return {Ofcs.Editor.Item.AbstractItem}
     */
    getRootItem() {
        Logger.debug('abstractItem.getRootItem()');

        return this.parentItem
            ? this.parentItem.getRootItem()
            : this;
    },

    /**
     * Sets the parent element. This is used on group childrens to connect
     * them with its group.
     *
     * @param {AbstractItem} parent
     */
    setParent(parent) {
        this.parent = parent;
    },

    /**
     * Returns the parent element of this item. Is primary used to retrieve
     * the group on one of its children.
     *
     * @return {AbstractItem}
     */
    getParent() {
        return this.parent;
    },

    /**
     * Returns all ancestor group items
     *
     * @return {AbstractItem[]}
     */
    getAncestorGroups() {
        if (!this.parent) {
            return [];
        }

        return this.parent.getAncestorGroups().concat(this.parent);
    },

    /**
     * Returns if the item has a parent item. Is primary used to check if
     * the item is child of a group.
     *
     * @return {boolean}
     */
    hasParent() {
        return this.parent !== null;
    },

    _sanitizeRotationValue(attrsParam) {
        const attrs = {
            ...attrsParam,
            rotation: (360 + attrsParam.rotation) % 360,
        };

        const isNumeric = !Number.isNaN(Number.parseFloat(attrs.rotation ?? '')) && Number.isFinite(attrs.rotation);

        if (!isNumeric) {
            delete attrs.rotation;
        }
        return attrs;
    },

    _updatePositionByRotationCenter(attrs, center) {
        const matrix = new Matrix();
        const position = attrs.position || this.get('position');

        matrix
            .rotate(this.get('rotation'), center)
            .invert() // Redo the original rotation
            .rotate(attrs.rotation, center); // and rotate to new rotation value

        return { ...attrs, position: position.clone().transform(matrix) };
    },

    getPathByFlippingStatus(pathName) {
        if (this.get('flipped')) {
            return this.getFlippedPath(pathName);
        }
        return this.get(pathName);
    },

    getBagLines() {
        return this.cache.get('bagLines', () => this.getLines(this.getPathByFlippingStatus('ext')));
    },

    getMarginLines() {
        return this.cache.get('marginLines', () => this.getLines(this.getPathByFlippingStatus('safety')));
    },

    getOriginalLines() {
        return this.cache.get('origLines', () => this.getLines(this.getPathByFlippingStatus('orig')));
    },

    getAbsoluteBagLines() {
        const matrix = this.getAbsoluteMatrix();
        return this.getBagLines().map(line => line.transform(matrix));
    },

    getAbsoluteMarginLines() {
        const matrix = this.getAbsoluteMatrix();
        return this.getBagLines().map(line => line.transform(matrix));
    },

    /**
     * Returns a new bounding box based on the current *attributes* of the item
     *
     * @return {Bounds} Bounding box of the item
     */
    getBounds() {
        const bounds = this.cache.get('bounds', () => {
            let positionedBounds;

            positionedBounds = this.cache.get('baseBounds', () => {
                const segments = this.getPathByFlippingStatus('ext');
                const points = this.getPoints(segments);
                const firstPoint = points.shift();
                const baseBounds = new Bounds(firstPoint);

                points.forEach(point => baseBounds.extend(point));

                if (this.supportsPositioning) {
                    const matrix = new Matrix();
                    const position = this.get('position');

                    matrix.translate(-position.x, -position.y);
                    baseBounds.transform(matrix);
                }

                return baseBounds;
            });

            positionedBounds = positionedBounds.clone();

            if (this.supportsPositioning) {
                const matrix = new Matrix();
                const position = this.get('position');

                matrix.translate(position.x, position.y);
                positionedBounds.transform(matrix);
            }

            return positionedBounds;
        });

        return bounds;
    },

    getAbsoluteBounds() {
        const distance = new Distance(this.get('position'), this.getAbsolutePosition());
        return this.getBounds().clone().move(distance);
    },

    /**
     * Returns a new bounding box based on the current *attributes* of the item
     *
     * @return {Bounds} Bounding box of the item
     */
    getMarginBounds() {
        const bounds = this.cache.get('marginBounds', () => {
            let positionedBounds;

            positionedBounds = this.cache.get('baseMarginBounds', () => {
                const segments = this.getPathByFlippingStatus('safety');
                const points = this.getPoints(segments);
                const baseBounds = new Bounds(points.shift());

                points.forEach(point => baseBounds.extend(point));

                if (this.supportsPositioning) {
                    const matrix = new Matrix();
                    const position = this.get('position');

                    matrix.translate(-position.x, -position.y);
                    baseBounds.transform(matrix);
                }

                return baseBounds;
            });

            positionedBounds = positionedBounds.clone();

            if (this.supportsPositioning) {
                const matrix = new Matrix();
                const position = this.get('position');

                matrix.translate(position.x, position.y);
                positionedBounds.transform(matrix);
            }

            return positionedBounds;
        });

        return bounds;
    },

    getAbsoluteMarginBounds() {
        const distance = new Distance(this.get('position'), this.getAbsolutePosition());
        return this.getMarginBounds().clone().move(distance);
    },

    /**
     * Return full bounds
     *
     * @return {Bounds}
     */
    getFullBounds() {
        return this.getBounds();
    },

    /**
     * Return safety margin for this item
     *
     * @return {number}
     */
    getSafetyMargin() {
        return this.findParentItem('foam').getSafetyMarginByDepth(this.get('depth'));
    },

    /**
     * Returns a transformation matrix for the current rotation and position
     *
     * @return {Matrix} Transformation matrix
     */
    getMatrix() {
        const matrix = new Matrix();
        const rotation = this.get('rotation');
        const { x, y } = this.get('position');

        if (this.supportsPositioning) {
            matrix.translate(x, y);
        }

        if (this.supportsRotation) {
            matrix.rotate(rotation);
        }

        return matrix;
    },

    /**
     * Returns a transformation matrix for the current rotation and position
     *
     * @return {Matrix} Transformation matrix
     */
    getAbsoluteMatrix() {
        const matrix = new Matrix();
        const rotation = this.get('rotation');
        const { x, y } = this.getAbsolutePosition();

        if (this.supportsPositioning) {
            matrix.translate(x, y);
        }

        if (this.supportsRotation) {
            matrix.rotate(rotation);
        }

        return matrix;
    },

    /**
     * Move item by distance.
     *
     * @param {Distance} distance
     */
    move(distance) {
        let position;

        if (this.supportsPositioning) {
            position = this.get('position').clone();
            position.move(distance);
            position = this.set('position', position);
        }
    },

    /**
     * Creates an array of lines from a given segments array
     *
     * @protected
     * @param {Array} segments Array of segments
     * @return {Array} Line array (x1, y1, x2, y2)
     */
    getLines(segments) {
        const matrix = this.getMatrix();

        return segments
            .reduce((points, segment) => points.concat(segment), [])
            .map((point, idx, points) => {
                const nextPoint = points[idx + 1];
                if (!nextPoint || nextPoint[0] === 'M') return null; // Ignore last point, no line can be created for it

                const [action, x, y] = point;
                const [, nextX, nextY] = nextPoint;

                if (action !== 'M' && action !== 'L') throw new Error(`Received invalid segment: "${action}"`);

                const line = new Line(new Point(x, y), new Point(nextX, nextY));

                line.transform(matrix);
                return line;
            })
            .filter(line => !!line);
    },

    /**
     * Creates an array of points from a given segments array.
     *
     * @protected
     * @param {Array} segments Array of segments
     * @return {Array} Point array (x, y)
     */
    getPoints(segments) {
        const matrix = this.getMatrix();
        const points = [];

        if (segments) {
            for (let i = 0; i < segments.length; i += 1) {
                for (let j = 0; j < segments[i].length - 1; j += 1) {
                    if (segments[i][j][0] !== 'M' && segments[i][j][0] !== 'L') {
                        throw new Error(`Received invalid segment: "${segments[i][j][0]}"`);
                    }

                    const point = new Point(segments[i][j][1], segments[i][j][2]);
                    point.transform(matrix);
                    points.push(point);
                }
            }
        }

        return points;
    },

    /**
     * Get flipped path. Uses flippedPaths cache.
     *
     * @param {string} key
     *
     * @return {Array}
     */
    getFlippedPath(key) {
        if (undefined === this.flippedPaths[key]) {
            this.flippedPaths[key] = this.flipPathSegments(this.attributes[key]);
        }

        return this.flippedPaths[key];
    },

    /**
     * Flip all segments of a path.
     *
     * @param {Array} segments
     *
     * @return {Array}
     */
    flipPathSegments(segments) {
        const segmentsLength = segments.length;
        const flippedSegments = new Array(segmentsLength);

        for (let i = 0; i < segmentsLength; i += 1) {
            flippedSegments[i] = this.flipPath(segments[i]);
        }

        return flippedSegments;
    },

    /**
     * Flip all entries of a path.
     *
     * @param {Array} path
     *
     * @return {Array}
     */
    flipPath(path) {
        const pathLength = path.length;
        const flippedPath = new Array(pathLength);

        for (let i = 0; i < pathLength; i += 1) {
            flippedPath[i] = this.flipPathEntry(path[i]);
        }

        return flippedPath;
    },

    /**
     * Flip a single path entry.
     *
     * @param {Array} entry
     * @return {Array}
     */
    flipPathEntry(entry) {
        const entryLength = entry.length;

        if (entryLength !== 3) {
            throw new Error(`Expecting length 3 for path entry, found ${entryLength}.`);
        }

        const [command, x, y] = entry;

        if (command !== 'M' && command !== 'L') {
            throw new Error(`Supported SVG path commands are L and M, found ${command}.`);
        }

        return [command, x, -y];
    },

    /**
     * Clear cache on change.
     *
     * @param {Object} changes
     */
    clearCache(changes) {
        if (!this.cache) return;

        if (changes.width !== undefined || changes.height !== undefined || changes.rotation !== undefined) {
            this.flippedPaths = {};
        }

        if (changes.width !== undefined || changes.height !== undefined || changes.rotation !== undefined
            || changes.flipped !== undefined) {
            this.cache.remove('baseBounds');
            this.cache.remove('baseMarginBounds');
            this.cache.remove('bounds');
            this.cache.remove('marginBounds');
            this.cache.remove('bagLines');
            this.cache.remove('marginLines');
            this.cache.remove('origLines');
            this.cache.remove('bagTree');
            this.cache.remove('marginTree');
        }

        if (changes.position !== undefined) {
            this.cache.remove('bounds');
            this.cache.remove('marginBounds');
            this.cache.remove('bagLines');
            this.cache.remove('marginLines');
            this.cache.remove('origLines');
            this.cache.remove('bagTree');
            this.cache.remove('marginTree');
        }

        if (changes.depth !== undefined) {
            this.cache.remove('baseMarginBounds');
            this.cache.remove('marginBounds');
            this.cache.remove('marginLines');
            this.cache.remove('bagTree');
            this.cache.remove('marginTree');
        }
    },

    /**
     * Clear cache of clone.
     *
     * @return {Ofcs.Editor.Item.AbstractItem}
     */
    clone(...args) {
        const clone = Model.prototype.clone.apply(this, args);

        clone.cache = new Cache();

        return clone;
    },

    /**
     * @param {ValidatorInterface} validator
     */
    addValidator(validator) {
        ValidatorInterface.assert(validator);

        if (this.validators.indexOf(validator) < 0) {
            this.validators.push(validator);
        }
    },

    /**
     * Validate the model values.
     *
     * @param {Object} values
     *
     * @return {Object|null}
     */
    validate(values) {
        const errors = {};

        if (!values) {
            return null;
        }

        this.validators.forEach(validator => Object.assign(errors, validator.validate(this, values)));

        if (undefined !== values.top && !this.isValidTop(values.top)) {
            errors.top = { tpl: 'error_invalid' };
        }

        if (undefined !== values.left && !this.isValidLeft(values.left)) {
            errors.left = { tpl: 'error_invalid' };
        }

        return Object.values(errors).length ? errors : null;
    },

    isValidLeft(value) {
        const parentItemWidth = this.getParentItem().get('width');
        const minValue = -500;
        const maxValue = parentItemWidth + 500;

        return value <= maxValue && value >= minValue;
    },

    isValidTop(value) {
        const parentItemHeight = this.getParentItem().get('height');
        const minValue = -500;
        const maxValue = parentItemHeight + 500;

        return value <= maxValue && value >= minValue;
    },

    /**
     * Calculate the rotation by considering the initial rotation
     *
     * @return {number}
     */
    getNormalizedRotation() {
        let rotation = this.get('rotation');

        if (this.supportsInitialRotation) {
            rotation -= this.get('initialRotation') || 0;
            rotation = (360 + rotation) % 360;
        }

        return rotation;
    },

    /**
     * Returns the area that an item covers
     *
     * @return {number}
     */
    getArea() {
        return 0;
    },

    getPosition() {
        return this.get('position');
    },

    getAbsolutePosition() {
        const position = this.get('position').clone();

        if (this.hasParentItem()) {
            const { x, y } = this.getParentItem().getAbsolutePosition();
            position.move(new Distance(x, y));
        }

        return position;
    },

    getBagTree() {
        return this.cache.get('bagTree', () => {
            if (!this.bagTree) {
                this.bagTree = new Tree(
                    this.cid,
                    'bag',
                    this,
                    this.getBounds(),
                    this.getBagLines(),
                    5,
                );

                return this.bagTree;
            }

            return this.bagTree.update(this.getBounds(), this.getBagLines());
        });
    },

    getMarginTree() {
        return this.cache.get('marginTree', () => {
            if (!this.marginTree) {
                this.marginTree = new Tree(
                    this.cid,
                    'margin',
                    this,
                    this.getMarginBounds(),
                    this.getMarginLines(),
                    5,
                );

                return this.marginTree;
            }

            return this.marginTree.update(this.getMarginBounds(), this.getMarginLines());
        });
    },

    getSupportedRotationStep(rotation) {
        const normalizedRotation = rotation % 360;
        const steps = this.supportedRotationSteps || [];
        let closestSnapDegree = null;
        let snapDegreeDistance = Math.max(...steps);

        steps.forEach((step) => {
            const distance = Math.abs(step - normalizedRotation);
            if (distance < snapDegreeDistance) {
                closestSnapDegree = step;
                snapDegreeDistance = distance;
            }
        });

        return closestSnapDegree;
    },

    /**
     * @return {boolean}
     */
    supportsSnappedRotation() {
        return Array.isArray(this.supportedRotationSteps) && this.supportedRotationSteps.length > 0;
    },

    /**
     * Check if one of the given properties has changed
     *
     * @param {...string|string[]} properties
     * @return {boolean}
     */
    hasChangedProperty(...properties) {
        const flattenedProperties = properties
            .reduce((keys, propertyParam) => {
                let property = propertyParam;
                if (!Array.isArray(property)) property = [property];
                return keys.concat(property);
            }, []);

        return Object
            .keys(this.changed)
            .some(key => flattenedProperties.indexOf(key) >= 0);
    },

    /**
     * Translate object into json stringable types. This method will be used
     * by JSON.stringify() automaticly.
     *
     * @return {Object}
     */
    toJSON(...args) {
        const attributes = Model.prototype.toJSON.apply(this, args);

        delete attributes.orig;
        delete attributes.ext;
        delete attributes.safety;

        if (attributes.position) {
            attributes.x = attributes.position.x;
            attributes.y = attributes.position.y;
            delete attributes.position;
        }

        return attributes;
    },
});
